'use strict';

import { readPageData } from './page_data';

/**
 * Shared validation helpers for project update forms (new + edit + inline editor).
 *
 * Field limits are rendered by PHP into <script type="application/json"
 * id="project-update-limits">
 * (see dynamic/admin/projects/new_project_update.php, edit_project_update.php, and
 * dynamic/collab_project.php).  getLimits() reads that object so PHP's constants.php
 * is always the single source of truth — no limit values are hardcoded here.
 *
 * The fallback in getLimits() matches the current constants.php values and exists
 * solely so the module remains usable in isolated unit tests without a live page.
 */

/**
 * Returns the validation limits rendered by PHP (#project-update-limits).
 * Falls back to hardcoded defaults so the module stays testable in isolation.
 * @returns {{ maxTitle: number, maxBody: number, maxLinkLabel: number,
 *             maxLinkUrl: number, maxEditNote: number, maxCommentBody: number,
 *             maxImages: number }}
 */
export function getLimits() {
  return readPageData('project-update-limits') ?? {
    maxTitle: 200, maxBody: 2000,
    maxLinkLabel: 100, maxLinkUrl: 500,
    maxEditNote: 200, maxCommentBody: 1000,
    maxImages: 5,
  };
}

/**
 * Count UTF-8 bytes, matching PHP strlen() behaviour.
 * A plain ASCII char = 1 byte; é = 2 bytes; most emoji = 4 bytes.
 * @param {string} s
 * @returns {number}
 */
const byteLen = s => new TextEncoder().encode(s).length;

/**
 * Validate the project update form fields.
 *
 * Reads inputs directly from the DOM via name selectors so the caller doesn't
 * need to pass element references.
 *
 * @param {object}  [options]
 * @param {boolean} [options.requireEditNote=false]  When true, validates the
 *   edit_note field inside the edit form (used for late edits of announced updates).
 * @returns {string|null}  An error message string, or null if everything is valid.
 */
export function validateUpdateForm({ requireEditNote = false } = {}) {
  const { maxTitle, maxBody, maxLinkLabel, maxLinkUrl, maxEditNote } = getLimits();

  const titleEl    = document.querySelector('[name="title"]');
  const bodyEl     = document.querySelector('[name="body"]');
  const editNoteEl = document.querySelector('form#js-edit-form [name="edit_note"]');

  if (!titleEl) return null;

  // Title
  const title = titleEl.value.trim();
  if (title.length === 0)         return 'Title is required.';
  if (byteLen(title) > maxTitle)  return `Title must be at most ${maxTitle} bytes.`;

  // Body (optional)
  if (bodyEl) {
    const body = bodyEl.value.trim();
    if (byteLen(body) > maxBody)  return `Description must be at most ${maxBody} bytes.`;
  }

  // Edit note (required for late edits)
  if (requireEditNote && editNoteEl) {
    const editNote = editNoteEl.value.trim();
    if (editNote.length === 0)              return 'Edit note is required for late edits.';
    if (byteLen(editNote) > maxEditNote)    return `Edit note must be at most ${maxEditNote} bytes.`;
  }

  // Links
  const labelInputs = document.querySelectorAll('[name="link_label[]"]');
  const urlInputs   = document.querySelectorAll('[name="link_url[]"]');

  for (let i = 0; i < labelInputs.length; i++) {
    const label = labelInputs[i].value.trim();
    const url   = urlInputs[i]?.value?.trim() || '';

    // Skip completely empty pairs
    if (label.length === 0 && url.length === 0) continue;

    // Both fields must be filled together
    if (label.length === 0 || url.length === 0) {
      return 'Each link must have both a label and a URL.';
    }

    if (byteLen(label) > maxLinkLabel) {
      return `Link label must be at most ${maxLinkLabel} bytes.`;
    }

    if (byteLen(url) > maxLinkUrl) {
      return `Link URL must be at most ${maxLinkUrl} bytes.`;
    }

    // URL format + scheme allowlist (mirrors PHP FILTER_VALIDATE_URL behaviour)
    try {
      const parsed = new URL(url);
      if (!['http:', 'https:'].includes(parsed.protocol)) {
        return `Link URL must start with http:// or https://.`;
      }
    } catch {
      return `Invalid URL format: ${url}`;
    }
  }

  return null;
}

/**
 * Display a validation error message in the shared #js-validation-error element.
 * Scrolls the element into view so the user sees it even on long forms.
 * Falls back to console.error if the element is missing (defensive — shouldn't happen).
 * @param {string} msg
 */
export function showValidationError(msg) {
  const errorEl = document.getElementById('js-validation-error');
  if (!errorEl) {
    console.error('Validation error (display element #js-validation-error not found):', msg);
    return;
  }
  errorEl.textContent = msg;
  errorEl.style.display = 'block';
  errorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
}

/**
 * Hide the shared #js-validation-error element.
 */
export function hideValidationError() {
  const errorEl = document.getElementById('js-validation-error');
  if (!errorEl) return;
  errorEl.style.display = 'none';
}
